import styled from "styled-components";
import useHomeworkReportModal from "features/homework/hooks/useHomeworkReportModal";
import PLAY_SVG from "assets/images/play.svg";
import HomeworkContentModal from "./HomeworkContentModal";
import { report, student } from "shared/types/Homework";

// 초를 분:초 형식으로 변환하는 헬퍼 함수
const formatTime = (seconds: number): string => {
    if (seconds < 60) {
        return `${seconds}초`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
        return `${minutes}분`;
    }
    return `${minutes}분 ${remainingSeconds}초`;
};

interface HomeworkReportModal{
    selectedReport:report,
    selectedStudent:student,
    selectedLesson:string,
    onConfirmClickHandler:() => void,
}

const HomeworkReportModal = ({ selectedLesson, selectedReport, selectedStudent, onConfirmClickHandler}:HomeworkReportModal) => {
    const {wordProgress, sentenceProgress, videoProgress, reportSetting, onPartClickHandler, isOpenContentModal, contentList, onContentModalConfirmClickhandler} = useHomeworkReportModal(selectedLesson, selectedStudent, selectedReport);

    const renderWordProgress = () => {
        const list = [];
        if (!reportSetting?.word) {
            list.push(<TableRow key={3}><TableColumn>그림 퀴즈</TableColumn><TableColumn>과제 설정 X</TableColumn><TableColumn>-</TableColumn></TableRow>);
            return <TableBody>{list}</TableBody>;
        }

        if (reportSetting.word.focus) {
            list.push(<TableRow key={1}><TableColumn>단어 집중 학습</TableColumn><TableColumn>{wordProgress && wordProgress.focus ? "완료" : "미완료"}</TableColumn><TableColumn>{wordProgress && wordProgress.focus_elapsed_seconds ? formatTime(wordProgress.focus_elapsed_seconds) : "미완료"}</TableColumn></TableRow>);
        } else {
            list.push(<TableRow key={1}><TableColumn>단어 집중 학습</TableColumn><TableColumn>과제 설정 X</TableColumn><TableColumn>-</TableColumn></TableRow>);
        }
        if (reportSetting.word.write) {
            list.push(<TableRow key={2}><TableColumn>단어 쓰기 학습</TableColumn><TableColumn>{wordProgress && wordProgress.write? `${wordProgress.write_score}점` : "미완료"}</TableColumn><TableColumn>{wordProgress && wordProgress.write_elapsed_seconds ? formatTime(wordProgress.write_elapsed_seconds) : "미완료"}</TableColumn></TableRow>);
        } else {
            list.push(<TableRow key={2}><TableColumn>단어 쓰기 학습</TableColumn><TableColumn>과제 설정 X</TableColumn><TableColumn>-</TableColumn></TableRow>);
        }
        if (reportSetting.word.painting) {
            list.push(<TableRow key={3}><TableColumn>그림 퀴즈</TableColumn><TableColumn>{wordProgress && wordProgress.painting ? `${wordProgress.painting_score}점` : "미완료"}</TableColumn><TableColumn>{wordProgress && wordProgress.painting_elapsed_seconds ? formatTime(wordProgress.painting_elapsed_seconds) : "미완료"}</TableColumn></TableRow>);
        } else {
            list.push(<TableRow key={3}><TableColumn>그림 퀴즈</TableColumn><TableColumn>과제 설정 X</TableColumn><TableColumn>-</TableColumn></TableRow>);
        }

        return <TableBody>{list}</TableBody>;
    }

    const renderSentenceProgress = () => {
        const list = [];
        if (reportSetting?.sentence) {
            if(reportSetting.sentence.listening){
                list.push(<TableRow key={1}><TableColumn>문장 듣기 학습</TableColumn><TableColumn>{sentenceProgress && sentenceProgress.listening ? "완료" : "미완료"}</TableColumn><TableColumn>{sentenceProgress && sentenceProgress.listening_elapsed_seconds ? formatTime(sentenceProgress.listening_elapsed_seconds) : "미완료"}</TableColumn></TableRow>)
            }else{
                list.push(<TableRow key={1}><TableColumn>문장 듣기 학습</TableColumn><TableColumn>과제 설정 X</TableColumn><TableColumn>-</TableColumn></TableRow>)
            }
            if(reportSetting.sentence.record){
                list.push(<TableRow key={2}><TableColumn>문장 녹음 학습</TableColumn><TableColumn>{sentenceProgress && sentenceProgress.record ? <DetailButton src={PLAY_SVG} alt="detail" onClick={() => onPartClickHandler(sentenceProgress.record_list)}/> : "미완료"}</TableColumn><TableColumn>{sentenceProgress && sentenceProgress.record_elapsed_seconds ? formatTime(sentenceProgress.record_elapsed_seconds) : "추적 X"}</TableColumn></TableRow>)
            }else{
                list.push(<TableRow key={1}><TableColumn>문장 녹음 학습</TableColumn><TableColumn>과제 설정 X</TableColumn><TableColumn>-</TableColumn></TableRow>)
            }
            if(reportSetting.sentence.order){
                list.push(<TableRow key={3}><TableColumn>어순 퀴즈</TableColumn><TableColumn>{sentenceProgress && sentenceProgress.order ? `${sentenceProgress.order_score}점` : "미완료"}</TableColumn><TableColumn>{sentenceProgress && sentenceProgress.order_elapsed_seconds ? formatTime(sentenceProgress.order_elapsed_seconds) : "미완료"}</TableColumn></TableRow>)
            }else{
                list.push(<TableRow key={1}><TableColumn>어순 퀴즈</TableColumn><TableColumn>과제 설정 X</TableColumn><TableColumn>-</TableColumn></TableRow>)
            }
        }
        return <TableBody>
            {list}
        </TableBody>
    }

    const renderVideoProgress = () => {
        const list = [];
        if (reportSetting?.video) {
            if(reportSetting.video.video){
                list.push(<TableRow key={1}><TableColumn>영상 더빙 학습</TableColumn><TableColumn>{videoProgress && videoProgress.video ? <DetailButton src={PLAY_SVG} alt="detail" onClick={() => onPartClickHandler(videoProgress.video_record_list)}/> : "미완료"}</TableColumn><TableColumn>{videoProgress && videoProgress.video_elapsed_seconds ? formatTime(videoProgress.video_elapsed_seconds) : "추적 X"}</TableColumn></TableRow>)
            }else{
                list.push(<TableRow key={1}><TableColumn>영상 더빙 학습</TableColumn><TableColumn>과제 설정 X</TableColumn><TableColumn>-</TableColumn></TableRow>)
            }
        }
        return <TableBody>
            {list}
        </TableBody>
    }
    
    if(!reportSetting){
        return null;
    }

    return(
        <Container>
            <HomeworkContentModal isOpen={isOpenContentModal} contentList={contentList} onConfirmClickHandler={onContentModalConfirmClickhandler} />
            <Modal>
                <TitleWrap>
                    <Title>{selectedReport.title}</Title>
                </TitleWrap>
                <Content>
                    <Progress>
                        <Table>
                            <TableHeader>
                                <TableColumn >단어 학습</TableColumn>
                                <TableColumn >수행 내역</TableColumn>
                                <TableColumn >수행 시간</TableColumn>
                            </TableHeader>
                            {renderWordProgress()}
                        </Table>
                    </Progress>
                    <Progress>
                        <Table>
                            <TableHeader>
                                <TableColumn >문장 학습</TableColumn>
                                <TableColumn >수행 내역</TableColumn>
                                <TableColumn >수행 시간</TableColumn>
                            </TableHeader>
                            {renderSentenceProgress()}
                        </Table>
                    </Progress>
                    <Progress>
                        <Table>
                            <TableHeader>
                                <TableColumn >영상 학습</TableColumn>
                                <TableColumn >수행 내역</TableColumn>
                                <TableColumn >수행 시간</TableColumn>
                            </TableHeader>
                            {renderVideoProgress()}
                        </Table>
                    </Progress>
                </Content>
                <ButtonWrap>
                    <Button onClick={onConfirmClickHandler}>닫기</Button>
                </ButtonWrap>
            </Modal>
        </Container>
    )
}

const Container = styled.div`
    position: fixed;
    top:0;
    left: 0;
    right: 0;
    bottom: 0;
    min-height: 100vh;
    min-width: 100vw;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 3000;
    padding: 24px;
    backdrop-filter: blur(4px);
`

const Modal = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    padding: 32px;
    flex-grow:1;
    height: 100%;
    max-width: 1200px;
    min-width: 400px;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1);
    animation: fadeInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(255, 255, 255, 0.2);

    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }
`

const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;
  width: 100%;
`;

const Title = styled.div`
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.5px;
  line-height: 1.2;
`;

const ButtonWrap = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: center;
    width: 100%;
`

const Button = styled.button`
    height: 56px;
    padding: 0 48px;
    background: #DF0000;
    color: white;
    border: none;
    border-radius: 16px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    transition: all 0.2s ease;
    box-shadow: 0 4px 16px rgba(223, 0, 0, 0.22);
    letter-spacing: 0.5px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(223, 0, 0, 0.28);
        background: #B91C1C;
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 4px 16px rgba(223, 0, 0, 0.22);
    }
`;

const Content = styled.div`
    display: flex;
    gap: 24px;
    flex-grow: 1;
    width: 100%;
    min-height: 0;
`

const Progress = styled.div`
    flex-basis: 100%;
    flex-grow: 1;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.06);
    overflow: hidden;
`

const Table = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-grow: 1;
    height: 100%;
`

const TableHeader = styled.div`
    display: flex;
    background: #0F172A;
    color: white;
    font-weight: 600;
    letter-spacing: 0.5px;
`

const TableColumn = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    border-right: 1px solid rgba(255, 255, 255, 0.2);
    padding: 16px 12px;
    font-size: 14px;
    line-height: 1.4;
    
    &:first-child{
        border-left: none;
        width: 40%;
        justify-content: flex-start;
        padding-left: 20px;
    }
    &:nth-child(2) {
        width: 35%;
    }
    &:last-child {
        width: 25%;
        border-right: none;
    }
`

const TableBody = styled.div`
    flex-grow: 1;
    height: 400px;
    min-height: 400px;
    overflow: auto;
    background: white;

    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
    }
`

const TableRow = styled.div<{selected?:boolean}>`
    display: flex;
    border-bottom: 1px solid #f0f0f0;
    background-color: ${(props) => props.selected ? "#FEF2F2" : "white"};
    font-weight: ${(props) => props.selected ? 600 : 400};
    transition: all 0.2s ease;
    
    &:hover{
        background-color: #FEF2F2;
    }

    &:last-child {
        border-bottom: none;
    }
`

const DetailButton = styled.img`
    width: 28px;
    height: 28px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 6px;
    padding: 4px;
    
    &:hover {
        background-color: #f0f0f0;
        transform: scale(1.1);
    }
`

export default HomeworkReportModal;